import { useCallback, useState } from "react";
import { Share2, Loader2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Share the current battlefield view as a LINK, not a PNG.
 *
 * Priority:
 *  1. `navigator.share({ url, title, text })` (native share sheet — iOS, Android,
 *     desktop Safari, some Chromium builds).
 *  2. `navigator.clipboard.writeText(url)` + "Link copied" toast.
 *  3. Last-resort inline dialog with a pre-selected text input the user can
 *     copy manually (insecure context / very old browsers).
 */
export default function ShareButton({ pair, iconOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState(null); // shows manual-copy dialog when set

  const handleShare = useCallback(async () => {
    if (busy) return;
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}/?pair=${pair.symbol}`
        : `/?pair=${pair.symbol}`;
    const title = `${pair.base}/${pair.quote} — Crypto Battlefield`;
    const text = `Watch bulls vs bears fight over ${pair.base}/${pair.quote} live.`;

    setBusy(true);
    // 1) Native share sheet
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
          await navigator.share({ url, title, text });
          setBusy(false);
          return;
        } catch (err) {
          // User cancel = AbortError — silent success, no toast, no fallback.
          if (err?.name === "AbortError") {
            setBusy(false);
            return;
          }
          // Any other error: fall through to clipboard path.
        }
      }

      // 2) Clipboard
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied", { description: url });
          setBusy(false);
          return;
        } catch {
          /* fall through */
        }
      }

      // 3) Manual dialog
      setFallbackUrl(url);
    } finally {
      setBusy(false);
    }
  }, [busy, pair]);

  return (
    <>
      {iconOnly ? (
        <button
          type="button"
          onClick={handleShare}
          disabled={busy}
          className="cb-hud-chip flex items-center justify-center w-11 h-11 hover:bg-white/5 disabled:opacity-50 transition-colors shrink-0"
          aria-label="Share this battle"
          data-testid="share-battle-button"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin text-white" />
          ) : (
            <Share2 className="w-4 h-4 text-white" />
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleShare}
          disabled={busy}
          className="cb-hud-chip flex items-center gap-2 px-4 py-2 hover:bg-white/5 disabled:opacity-50 transition-colors group"
          aria-label="Share this battle"
          data-testid="share-battle-button"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin text-white" />
          ) : (
            <Share2 className="w-4 h-4 text-white/80 group-hover:text-white" />
          )}
          <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-white">
            Share
          </span>
        </button>
      )}

      {fallbackUrl && (
        <ManualCopyDialog url={fallbackUrl} onClose={() => setFallbackUrl(null)} />
      )}
    </>
  );
}

function ManualCopyDialog({ url, onClose }) {
  const [copied, setCopied] = useState(false);
  const selectAll = (e) => {
    e.currentTarget.select();
  };
  const tryCopyAgain = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* stays manual */
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Copy share link"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      data-testid="share-fallback-dialog"
    >
      <div
        className="cb-hud-chip max-w-md w-full p-5 flex flex-col gap-3 pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-300">
            Copy this link
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="share-fallback-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          readOnly
          value={url}
          onFocus={selectAll}
          onClick={selectAll}
          className="w-full bg-black/60 border border-white/15 rounded-sm px-3 py-2 font-mono-tac text-xs text-white outline-none"
          data-testid="share-fallback-input"
        />
        <button
          type="button"
          onClick={tryCopyAgain}
          className="cb-hud-chip flex items-center justify-center gap-2 px-4 py-2 min-h-[40px] hover:bg-white/10 transition-colors font-mono-tac text-[10px] uppercase tracking-[0.24em] text-white"
          data-testid="share-fallback-copy"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-[#00FFA3]" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy link
            </>
          )}
        </button>
      </div>
    </div>
  );
}
